import useManageProjectStore from '../../../stores/manageProject/useManageProjectStore'
import authenStore from '../../../stores/authen/authenStore'
import constUserGroup from '../../../constants/userGroup'
import useCheckPermission from '../../../utilities/checkPermission'
import constSubmittedStep from '../../../constants/submittedStep'
import useWebService from '../../../services/web/useWebService'
import useFolderAndTab from '../project/useFolderAndTab'
import useLoading from '../../../control/loading/useLoading'
import { FormApi } from 'final-form'
import { Dispatch, SetStateAction, useRef } from 'react'
import { validateModeAccordionError } from '../../../utilities/validate'
import useAppFolderAndTabStore from '../../../stores/appFolderAndTab/appFolderAndTabStore'

const useEditSynthesis = (props: {
    form: FormApi<Record<string, any>, Partial<Record<string, any>>>
    setIsEditSynthesisSuccess?: Dispatch<SetStateAction<boolean>>
}) => {
    const { form, setIsEditSynthesisSuccess } = props
    const manageProjectStore = useManageProjectStore()
    const profileStore = authenStore()
    const checkPermission = useCheckPermission()
    const callWebService = useWebService()
    const tabAndFolderHook = useFolderAndTab()
    const loadingHook = useLoading()
    const editingInitial = useRef<{ [key: string]: any }>({})
    const { tabSelected } = useAppFolderAndTabStore()

    const generateApproveStatusParams = () => {
        const currentFormValues = form.getState().values
        const submittedID = manageProjectStore?.projectData?.submittedID ?? null
        const projectIRB = manageProjectStore?.projectData?.irb ?? null

        return {
            loginUserID: profileStore.profile.userId,
            submittedID: submittedID,
            reviewerID:
                profileStore.profile.userGroupId === constUserGroup.officer
                    ? currentFormValues && currentFormValues.reviewer && currentFormValues.reviewer.value
                        ? parseInt(currentFormValues?.reviewer.value)
                        : null
                    : profileStore.profile.userId,
            approveStatusID:
                currentFormValues && currentFormValues.approveStatus && currentFormValues.approveStatus.value
                    ? parseInt(currentFormValues?.approveStatus.value)
                    : null,
            irb: projectIRB,
            progress_6_months: currentFormValues.progress_6_months,
            progress_12_months: currentFormValues.progress_12_months,
        }
    }

    const updateApproveStatus = (fieldNames: string[]) => {
        const params = generateApproveStatusParams()
        const formProps = form.getState()
        const isError = validateModeAccordionError(fieldNames, formProps)
        return new Promise((resolve, reject) => {
            if (!isError) {
                Promise.all([callWebService.updateSynthesisApproveStatus(params)])
                    .then(async () => {
                        await loadTabAndFolder()
                        resolve('')
                    })
                    .catch(() => {
                        reject('')
                    })
            } else {
                form.submit()
                reject('')
            }
        })
    }

    const generateCommentaryParams = (name: string) => {
        const currentFormValues = form.getState().values
        const submittedID = manageProjectStore?.projectData?.submittedID ?? null
        const edittingVal = form.getFieldState(name)?.value
        let synthesisInfo: any[] = edittingVal?.map((data: any) => ({
            topicID: data?.id ?? null,
            contentHtml: data?.synthesisList?.contentHtml ?? '<p></p>',
            contentText: data?.synthesisList?.contentText ?? '',
        }))

        return {
            loginUserID: profileStore.profile.userId,
            submittedID: submittedID,
            reviewerID:
                profileStore.profile.userGroupId === constUserGroup.officer
                    ? currentFormValues && currentFormValues.reviewer && currentFormValues.reviewer.value
                        ? parseInt(currentFormValues?.reviewer.value)
                        : null
                    : profileStore.profile.userId,
            synthesisInfo: synthesisInfo ?? [],
        }
    }

    const updateCommentary = (name: string) => {
        const params = generateCommentaryParams(name)
        const formProps = form.getState()
        const isError = validateModeAccordionError([name], formProps)
        return new Promise((resolve, reject) => {
            if (!isError) {
                Promise.all([callWebService.updateSynthesisCommentaryTopic(params)])
                    .then(async () => {
                        await loadTabAndFolder()
                        if (setIsEditSynthesisSuccess) {
                            setIsEditSynthesisSuccess((prev) => !prev)
                        }
                        resolve('')
                    })
                    .catch(() => {
                        reject('')
                    })
            } else {
                form.submit()
                reject('')
            }
        })
    }

    const conditionUseAccordion = () => {
        const currentStep = manageProjectStore?.projectData?.stepID ?? null
        const tabStep = tabSelected?.stepID ?? null
        const isOfficer = checkPermission.isUserAdmin()
        const isChairman = checkPermission.isUserChairmanOnly()
        const isViceChairman = checkPermission.isUserViceChairman()
        const isSecretary = checkPermission.isUserSecretary()
        const checkStep =
            tabStep === constSubmittedStep.commentary
                ? false
                : tabStep === constSubmittedStep.synthesis
                ? currentStep === constSubmittedStep.synthesisReview
                : null
        return (isOfficer || isChairman || isViceChairman || isSecretary) && checkStep
    }

    const loadTabAndFolder = async () => {
        loadingHook.loadingOpen()
        if (manageProjectStore?.projectData) {
            await tabAndFolderHook.reloadProjectData(
                manageProjectStore.projectData.projectID,
                manageProjectStore.projectData.submittedID,
            )
        }
        loadingHook.loadingClose()
    }

    const resetField = (name: string, callback: (data: any) => void) => {
        form.change(name, editingInitial.current[name])
        callback(
            form.getFieldState(name)?.value?.map((data: any) => ({
                ...data,
                synthesisList: {
                    contentHtml: data?.synthesisList?.contentHtml ?? '<p></p>',
                    contentValue: data?.synthesisList?.contenText ?? '',
                },
            })),
        )
    }

    const confirmField = (name: string, callback: (data: any) => void) => {
        callback(
            form.getFieldState(name)?.value?.map((data: any) => ({
                ...data,
                synthesisList: {
                    contentHtml: data?.synthesisList?.contentHtml ?? '<p></p>',
                    contentValue: data?.synthesisList?.contenText ?? '',
                },
            })),
        )
    }

    const defineValueFromInit = (name: string) => {
        return new Promise((resolve) => {
            const currentFieldValue = form.getFieldState(name)?.value
            const fieldInitialValue = form.getFieldState(name)?.initial
            if (!currentFieldValue) {
                editingInitial.current[name] = fieldInitialValue
            } else {
                editingInitial.current[name] = currentFieldValue
            }
            resolve('')
        })
    }

    return {
        useAccordionMode: conditionUseAccordion(),
        updateApproveStatus,
        defineValueFromInit,
        resetField,
        confirmField,
        updateCommentary,
    }
}

export default useEditSynthesis
